'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';

import { Button } from '@/components/Default/Button';
import { Content } from '@/components/Default/Content';
import { Header } from '@/components/Default/Title/Header';
import { Section } from '@/components/Default/Section/Section';
import { Title } from '@/components/Default/Title/Title';
import { Description } from '@/components/Default/Title/Description';

const Banner = () => {
    const [isInViewport, setInViewport] = useState<boolean>(false);

    return (
        <Section className='bg-primary-200 h-[72rem] lg:h-[60rem] overflow-y-hidden overflow-x-hidden my-0' onInViewport={(inViewport: boolean) => setInViewport(inViewport)} inViewOptions={{ once: true }}>
            <div className='absolute top-0 left-0 w-full h-full flex items-center'>
                <div className='w-full max-w-screen-2xl mx-auto px-4 sm:px-8 md:px-16 rounded-2xl text-center'>
                    <Title className='max-w-screen-lg' direction='vertical'>
                        <div>
                            <svg className='inline-block w-32 h-32 md:w-auto md:h-auto' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' width={256} height={256}>
                                <linearGradient id='5W~O5nGU7nOUp6vx_KxUVa' x1='24' x2='24' y1='5.834' y2='42.755' gradientUnits='userSpaceOnUse'>
                                    <stop offset='0' stopColor='#fede00'/>
                                    <stop offset='1' stopColor='#ffd000'/>
                                </linearGradient>
                                <motion.path
                                    initial={{
                                        opacity: 0,
                                        y: '100%'
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        y: isInViewport ? '0%' : '100%'
                                    }}
                                    transition={{
                                        delay: 0.65
                                    }}
                                    fill='url(#5W~O5nGU7nOUp6vx_KxUVa)' d='M24,40.869V28h-9.198c-1.308,0-2.176-1.356-1.628-2.544L21.85,6.659	C22.356,5.562,24,5.923,24,7.131V20h9.198c1.308,0,2.176,1.356,1.628,2.544L26.15,41.341C25.644,42.438,24,42.077,24,40.869z'
                                />
                                <motion.line
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                        x: -15
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        scale: isInViewport ? 1 : 0,
                                        x: isInViewport ? 0 : -15
                                    }}
                                    transition={{
                                        delay: 0.85
                                    }} x1='43' x2='40' y1='24' y2='24' fill='none' stroke='#e36001' strokeLinecap='round' strokeLinejoin='round' strokeMiterlimit='10' strokeWidth='2' />
                                <motion.line
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                        x: 15
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        scale: isInViewport ? 1 : 0,
                                        x: isInViewport ? 0 : 15
                                    }}
                                    transition={{
                                        delay: 0.85
                                    }} x1='8' x2='5' y1='24' y2='24' fill='none' stroke='#e36001' strokeLinecap='round' strokeLinejoin='round' strokeMiterlimit='10' strokeWidth='2' />
                                <motion.line
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                        x: -15,
                                        y: 15
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        scale: isInViewport ? 1 : 0,
                                        x: isInViewport ? 0 : -15,
                                        y: isInViewport ? 0 : 15
                                    }}
                                    transition={{
                                        delay: 0.85
                                    }} x1='37.435' x2='35.314' y1='10.565' y2='12.686' fill='none' stroke='#e36001' strokeLinecap='round' strokeLinejoin='round' strokeMiterlimit='10' strokeWidth='2'/>
                                <motion.line
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                        x: 15,
                                        y: -15
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        scale: isInViewport ? 1 : 0,
                                        x: isInViewport ? 0 : 15,
                                        y: isInViewport ? 0 : -15
                                    }}
                                    transition={{
                                        delay: 0.85
                                    }} x1='12.686' x2='10.565' y1='35.314' y2='37.435' fill='none' stroke='#e36001' strokeLinecap='round' strokeLinejoin='round' strokeMiterlimit='10' strokeWidth='2'/>
                                <motion.line
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                        x: -15,
                                        y: -15
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        scale: isInViewport ? 1 : 0,
                                        x: isInViewport ? 0 : -15,
                                        y: isInViewport ? 0 : -15
                                    }}
                                    transition={{
                                        delay: 0.85
                                    }} x1='37.435' x2='35.314' y1='37.435' y2='35.314' fill='none' stroke='#e36001' strokeLinecap='round' strokeLinejoin='round' strokeMiterlimit='10' strokeWidth='2'/>
                                <motion.line
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                        x: 15,
                                        y: 15
                                    }}
                                    animate={{
                                        opacity: isInViewport ? 1 : 0,
                                        scale: isInViewport ? 1 : 0,
                                        x: isInViewport ? 0 : 15,
                                        y: isInViewport ? 0 : 15
                                    }}
                                    transition={{
                                        delay: 0.85
                                    }} x1='12.686' x2='10.565' y1='12.686' y2='10.565' fill='none' stroke='#e36001' strokeLinecap='round' strokeLinejoin='round' strokeMiterlimit='10' strokeWidth='2'/>
                            </svg>
                        </div>
                        <Header align='center'>
                            Overige diensten
                        </Header>
                        <Description align='center' className='mt-4 text-primary-950'>
                            Bij Time for Solar kunt u terecht voor al uw elektra- en zonne-energieprojecten. Of het nu gaat om de installatie van zonnepanelen, renovaties, of het oplossen van storingen, wij staan voor u klaar. Wij bedienen zowel particulieren als zakelijke klanten, dus aarzel niet om contact met ons op te nemen. Bij Time for Solar vindt u altijd de juiste specialist!
                        </Description>
                    </Title>
                    <div className='flex flex-col md:flex-row justify-around md:gap-x-16 bg-black text-white px-4 md:px-16 py-4 sm:py-8 md:py-16 rounded-2xl text-start'>
                        <ol className='mb-4'>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Stopcontacten
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Aardlekschakelaars
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Groepenkast uitbreiden
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Buitenverlichtingen aanleggen
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Lampen installeren
                            </li>
                        </ol>
                        <ol className='mb-4'>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Perilex aansluitingen
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Renovatie van elektra
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Schakelaars en bekabelingen
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Elektra problemen
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Elektronische installatie
                            </li>
                        </ol>
                        <ol className='mb-4'>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Kortsluitingen opsporen
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Laadpaal installeren
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Storingsdienst
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Krachtstroom aanleggen
                            </li>
                            <li className='flex gap-x-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                                </svg>
                                Inductiekookplaat aansluiten
                            </li>
                        </ol>
                    </div>
                    <Button href='/contact' label='Neem contact op' size='medium' animated={true} className='hover:bg-orange-700 hover:border-orange-700 ml-auto my-8' />
                </div>
            </div>
        </Section>
    );
};

export default Banner;